import React, { useState, useEffect, useRef } from 'react';
import './scoutOneLine.style.scss';
import { usePlayer } from '../../../../Contexts/playerData.context.jsx';
// eslint-disable-next-line
import { saveToDB, getNipList, getUserFromToken } from '../../../../utils/functions.ts';
// eslint-disable-next-line
import checkIcon from '../../../../assets/icons/QuickList.svg';
import checkIconOff from '../../../../assets/icons/NoQuickList.svg';
import { useDataLayer } from '../../../../Contexts/DataLayer.context.jsx';
import AddToBox from '../../../Boxes/AddToBox/addToBox.component.jsx';
import { FindCFLTeam } from '../../../../utils/loadFunctions.ts';
import { FindStatus } from '../../../../utils/loadFunctions.ts';
import { useAllThingsLists } from '../../../../Contexts/allThingsList.context.jsx';
import { useNavBar } from '../../../../Contexts/navbar.items.context.jsx';
import { useMask } from '../../../../Contexts/mask.context.jsx';
import { smoothScrollRowIntoView } from '../../../../utils/scrollUtils.js';

const DOUBLE_TAP_MS = 450;
const SHOW_SCROLLBARS_STORAGE_KEY = "FBX_ONE_LINER_SHOW_SCROLLBARS";
const SHOW_SCROLLBARS_EVENT = "FBX_ONE_LINER_SHOW_SCROLLBARS_CHANGE";

const hasBluBarsAccess = (user) => {
    const securityLevel = String(user?.userContactLevel ?? user?.security ?? user?.ADMIN ?? user?.admin ?? '').trim();
    return securityLevel === '1' || user?.ADMIN === true || user?.admin === true;
};

const ScoutOneLiner = () => {
    const { listArray, updateListArray } = useDataLayer();
    const { quickList, quickListNumbers, updateQuickListNumbers, updateQuickList } = useAllThingsLists();

    const {
        playerID,
        playerData,
        setPlayerData,
        listLoadID,
        setPlayerID,
        activePlayer,
        setActivePlayer
    } = usePlayer();

    const { updateWhichOneLine, activeSideButton, updateActiveSideButton } = useNavBar();
    const { showMask } = useMask();

    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState('asc');
    const [clickTimeout, setClickTimeout] = useState(null);
    const [rapidListID, setRapidListID] = useState(null);
    const [boxPosition, setBoxPosition] = useState({ top: 0, left: 0 });
    const [isAddOpen] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const lastTapRef = useRef({ playerID: '', time: 0 });
    const cancelAutoScrollRef = useRef(null);
    const [canOpenBluBars, setCanOpenBluBars] = useState(false);
    const [showScrollbars, setShowScrollbars] = useState(() => {
        try {
            return localStorage.getItem(SHOW_SCROLLBARS_STORAGE_KEY) === "On";
        } catch {
            return false;
        }
    });

    useEffect(() => {
        let isMounted = true;

        const loadBluBarsAccess = async () => {
            const user = await getUserFromToken();
            if (!isMounted) return;
            setCanOpenBluBars(hasBluBarsAccess(user));
        };

        loadBluBarsAccess();

        const handleFocus = () => loadBluBarsAccess();
        window.addEventListener('focus', handleFocus);

        return () => {
            isMounted = false;
            window.removeEventListener('focus', handleFocus);
        };
    }, []);

    useEffect(() => {
        const readScrollbarSetting = () => {
            try {
                setShowScrollbars(localStorage.getItem(SHOW_SCROLLBARS_STORAGE_KEY) === "On");
            } catch {
                setShowScrollbars(false);
            }
        };

        window.addEventListener(SHOW_SCROLLBARS_EVENT, readScrollbarSetting);
        window.addEventListener("storage", readScrollbarSetting);

        return () => {
            window.removeEventListener(SHOW_SCROLLBARS_EVENT, readScrollbarSetting);
            window.removeEventListener("storage", readScrollbarSetting);
        };
    }, []);

    useEffect(() => {
        if (!playerID) return;
        if (!Array.isArray(playerData) || playerData.length === 0) return;
        if (typeof setActivePlayer !== 'function') return;

        const activeData = playerData.find(
            item => String(item?.FBXID) === String(playerID)
        );

        if (
            activeData &&
            String(activePlayer?.FBXID || '') !== String(activeData.FBXID)
        ) {
            setActivePlayer(activeData);
        }
    }, [playerID, playerData, activePlayer?.FBXID, setActivePlayer]);

    useEffect(() => {
        if (Array.isArray(playerData) && playerData.length > 0) {
            const sorted = [...playerData].sort((a, b) => {
                const aValue =
                    isNaN(Number(a?.finalOut)) || ['0', ' ', null, '', '-'].includes(a?.finalOut)
                        ? 0
                        : Number(a.finalOut);

                const bValue =
                    isNaN(Number(b?.finalOut)) || ['0', ' ', null, '', '-'].includes(b?.finalOut)
                        ? 0
                        : Number(b.finalOut);

                return bValue - aValue; // highest report FINAL first
            });

            setPlayerData(sorted);
        }

        setSortField('finalOut');
        setSortOrder('desc');
        setSelectedIndex(0);
        updateWhichOneLine('Scout');
        // eslint-disable-next-line
    }, [listLoadID]);


    useEffect(() => {
        const activeList = Array.isArray(playerData) ? playerData : [];

        if (activeList.length === 0) {
            setSelectedIndex(0);
            return;
        }

        const refIndex = activeList.findIndex((item) => item === activePlayer);
        if (refIndex >= 0) {
            setSelectedIndex((prev) => (prev === refIndex ? prev : refIndex));
            return;
        }

        if (selectedIndex < 0 || selectedIndex >= activeList.length) {
            setSelectedIndex(0);
        }
    }, [playerData, activePlayer, selectedIndex]);

    useEffect(() => {
        const blockedButtons = new Set(['', 'notes', 'reports', 'reports1', 'Files']);

        const handleArrowNav = (e) => {
            if (showMask) return;
            if (blockedButtons.has(activeSideButton)) return;
            if (!Array.isArray(playerData) || playerData.length === 0) return;

            const tag = (e.target?.tagName || '').toLowerCase();
            const isTypingTarget =
                tag === 'input' ||
                tag === 'textarea' ||
                tag === 'select' ||
                e.target?.isContentEditable;

            if (isTypingTarget) return;
            if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;

            e.preventDefault();

            let nextIndex = selectedIndex;

            if (e.key === 'ArrowDown') {
                nextIndex = Math.min(selectedIndex + 1, playerData.length - 1);
            } else {
                nextIndex = Math.max(selectedIndex - 1, 0);
            }

            const nextPlayer = playerData[nextIndex];
            if (!nextPlayer) return;

            setSelectedIndex(nextIndex);
            setPlayerID(nextPlayer.FBXID);
            setActivePlayer(nextPlayer);

            requestAnimationFrame(() => {
                const container = document.querySelector(".one-liner-container .scrollable-content");
                const row = container?.querySelector(`[data-rowindex="${nextIndex}"]`);
                cancelAutoScrollRef.current?.();
                cancelAutoScrollRef.current = smoothScrollRowIntoView(row, { container });
            });
        };

        window.addEventListener('keydown', handleArrowNav);
        return () => window.removeEventListener('keydown', handleArrowNav);
    }, [activeSideButton, playerData, selectedIndex, setPlayerID, setActivePlayer, showMask]);

    useEffect(() => {
        return () => {
            cancelAutoScrollRef.current?.();
        };
    }, []);

    const handleCheckboxChange = (FBXID) => {
        if (clickTimeout) {
            clearTimeout(clickTimeout);
            setClickTimeout(null);
        } else {
            const timeout = setTimeout(() => {
                setClickTimeout(null);
            }, 100);
            setClickTimeout(timeout);

            const newNumberArray = quickListNumbers.includes(FBXID)
                ? quickListNumbers.filter((item) => item !== FBXID)
                : [...quickListNumbers, FBXID];

            updateQuickListNumbers(newNumberArray);
            quickList.theNumbers = newNumberArray;
            quickList.listTotal = newNumberArray.length;
            updateQuickList(quickList);
            saveToDB(FBXID, quickList.listId);

            const updatedListArray = listArray.map((item) => {
                if (item.listId === quickList.listId) {
                    return { ...item, ...quickList };
                }
                return item;
            });
            updateListArray(updatedListArray);
        }
    };

    const closeAddToBox = () => {
        setActivePlayer(null);
        setRapidListID(null);
    };

    const sort = (field) => {
        const order = field === sortField && sortOrder === 'asc' ? 'desc' : 'asc';

        const sorted = [...playerData].sort((a, b) => {
            let aValue;
            let bValue;

            switch (field) {
                case 'height':
                case 'weight':
                case 'speed40Fastest':
                case 'optionYr':
                case 'age':
                case 'jersey':
                case 'proJersey':
                case 'cflDraftYear':
                case 'nflDraftYear':
                case 'calculated':
                case 'finalOut':
                case 'masterGrade':
                    aValue = isNaN(Number(a[field])) || ['0', ' ', null, '', '-'].includes(a[field]) ? 0 : Number(a[field]);
                    bValue = isNaN(Number(b[field])) || ['0', ' ', null, '', '-'].includes(b[field]) ? 0 : Number(b[field]);
                    break;

                case 'report':
                    aValue = [' ', null, '', '-'].includes(a[field]) ? Infinity : a[field];
                    bValue = [' ', null, '', '-'].includes(b[field]) ? Infinity : b[field];
                    break;

                case 'cflDate':
                case 'nflDate':
                case 'reportDate': {
                    const parseDate = (date) =>
                        date === '-' || !date || isNaN(Date.parse(date)) ? 0 : Date.parse(date);

                    aValue = parseDate(a[field]);
                    bValue = parseDate(b[field]);
                    break;
                }

                case 'cflTeam':
                case 'nflTeam':
                case 'theEvent':
                    aValue = [' ', null, '', '-'].includes(a[field]) ? Infinity : a[field];
                    bValue = [' ', null, '', '-'].includes(b[field]) ? Infinity : b[field];
                    break;

                case 'last':
                    aValue = `${a.last || ''}, ${a.first || ''}`.toUpperCase();
                    bValue = `${b.last || ''}, ${b.first || ''}`.toUpperCase();
                    break;

                default:
                    aValue = a[field];
                    bValue = b[field];
                    break;
            }

            if (aValue === bValue) return 0;

            return order === 'desc'
                ? (aValue < bValue ? 1 : -1)
                : (aValue > bValue ? 1 : -1);
        });

        setPlayerData(sorted);
        setSortField(field);
        setSortOrder(order);
    };

    const isChecked = (FBXID) => quickListNumbers.includes(FBXID);

    const saveYLocation = (event, player, index) => {
        const row = event.target.closest('.nav-top-data');
        if (!row) return;

        const tappedPlayerID = String(player?.FBXID || '');
        const now = Date.now();
        const isDoubleTap =
            canOpenBluBars &&
            tappedPlayerID &&
            lastTapRef.current.playerID === tappedPlayerID &&
            now - lastTapRef.current.time <= DOUBLE_TAP_MS;
        lastTapRef.current = { playerID: tappedPlayerID, time: now };

        const rect = row.getBoundingClientRect();
        const boxWidth = 350;
        const offsetX = rect.left - 15;
        const newLeft = window.innerWidth - boxWidth - offsetX;

        setBoxPosition({ top: rect.y, left: newLeft });
        setSelectedIndex(index);

        setPlayerID(player.FBXID);
        setActivePlayer(player);

        if (isAddOpen) {
            setRapidListID(player.FBXID);
        }

        if (isDoubleTap) {
            updateActiveSideButton('Blue');
        }
    };

    return (
        <div className={`one-liner-container ${showScrollbars ? "show-scrollbars" : ""}`}>
            <div className="nav-top">
                <div className="col-1 text-center">
                    <img
                        title="Add/Remove QuickList"
                        src={checkIconOff}
                        alt="Checkbox Icon"
                        style={{ width: '24px', height: '24px' }}
                    />
                </div>

                <div
                    title="Lastname, Firstname Note"
                    onClick={() => sort('last')}
                    className={`col-5 ps-1 ${sortField === 'last' ? (sortOrder === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                    style={{ cursor: 'pointer' }}
                >
                    Last, First (NT)
                </div>

                <div
                    title="School"
                    onClick={() => sort('school')}
                    className={`text-center col-2 ${sortField === 'school' ? (sortOrder === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                    style={{ cursor: 'pointer' }}
                >
                    SCH
                </div>

                <div
                    title="General Position"
                    onClick={() => sort('genPos')}
                    className={`text-center col-1 ${sortField === 'genPos' ? (sortOrder === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                    style={{ cursor: 'pointer' }}
                >
                    CGP
                </div>

                <div
                    title="College Jersey"
                    onClick={() => sort('jersey')}
                    className={`text-center col-1 ${sortField === 'jersey' ? (sortOrder === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                    style={{ cursor: 'pointer' }}
                >
                    C#
                </div>

                <div
                    title="Pro Jersey"
                    onClick={() => sort('proJersey')}
                    className={`text-center col-1 ${sortField === 'proJersey' ? (sortOrder === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                    style={{ cursor: 'pointer' }}
                >
                    P#
                </div>

                <div
                    title="Height (Verified Code)"
                    onClick={() => sort('height')}
                    className={`text-center col-2 ${sortField === 'height' ? (sortOrder === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                    style={{ cursor: 'pointer' }}
                >
                    HT(V)
                </div>

                <div
                    title="Weight (Verified Code)"
                    onClick={() => sort('weight')}
                    className={`text-center col-2 ${sortField === 'weight' ? (sortOrder === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                    style={{ cursor: 'pointer' }}
                >
                    WT(V)
                </div>

                <div
                    title="Speed (Verified Code)"
                    onClick={() => sort('speed40Fastest')}
                    className={`text-center col-2 ${sortField === 'speed40Fastest' ? (sortOrder === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                    style={{ cursor: 'pointer' }}
                >
                    SP(V)
                </div>

                <div
                    title="CFL Team / Status"
                    onClick={() => sort('cflTeam')}
                    className={`text-center col-2 ${sortField === 'cflTeam' ? (sortOrder === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                    style={{ cursor: 'pointer' }}
                >
                    CFL
                </div>

                <div
                    title="CFL Date"
                    onClick={() => sort('cflDate')}
                    className={`text-center col-2 ${sortField === 'cflDate' ? (sortOrder === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                    style={{ cursor: 'pointer' }}
                >
                    CFL-Date
                </div>

                <div
                    title="Final Contract Year"
                    onClick={() => sort('optionYr')}
                    className={`text-center col-1 ${sortField === 'optionYr' ? (sortOrder === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                    style={{ cursor: 'pointer' }}
                >
                    FCY
                </div>

                <div
                    title="CFL Draft Year"
                    onClick={() => sort('cflDraftYear')}
                    className={`text-center col-1 ${sortField === 'cflDraftYear' ? (sortOrder === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                    style={{ cursor: 'pointer' }}
                >
                    DYR
                </div>

                <div
                    title="NFL Team / Status"
                    onClick={() => sort('nflTeam')}
                    className={`text-center col-2 ${sortField === 'nflTeam' ? (sortOrder === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                    style={{ cursor: 'pointer' }}
                >
                    NFL
                </div>

                <div
                    title="NFL Date"
                    onClick={() => sort('nflDate')}
                    className={`text-center col-2 ${sortField === 'nflDate' ? (sortOrder === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                    style={{ cursor: 'pointer' }}
                >
                    NFL-Date
                </div>

                <div
                    title="NFL Draft Year"
                    onClick={() => sort('nflDraftYear')}
                    className={`text-center col-1 ${sortField === 'nflDraftYear' ? (sortOrder === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                    style={{ cursor: 'pointer' }}
                >
                    DYR
                </div>

                <div
                    title="UFL24 Team / Status"
                    onClick={() => sort('ufl24Team')}
                    className={`text-center col-3 ${sortField === 'ufl24Team' ? (sortOrder === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                    style={{ cursor: 'pointer' }}
                >
                    UFL 24
                </div>

                <div
                    title="Attended Event"
                    onClick={() => sort('theEvent')}
                    className={`text-center col-2 ${sortField === 'theEvent' ? (sortOrder === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                    style={{ cursor: 'pointer' }}
                >
                    Event
                </div>

                <div
                    title="Calculated Value"
                    onClick={() => sort('calculated')}
                    className={`text-center col-1 ${sortField === 'calculated' ? (sortOrder === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                    style={{ cursor: 'pointer' }}
                >
                    FC
                </div>

                <div
                    title="Report Grade"
                    onClick={() => sort('finalOut')}
                    className={`text-center col-1 ${sortField === 'finalOut' ? (sortOrder === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                    style={{ cursor: 'pointer' }}
                >
                    RG
                </div>

                <div
                    title="Report Date"
                    onClick={() => sort('reportDate')}
                    className={`text-center col-3 ${sortField === 'reportDate' ? (sortOrder === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                    style={{ cursor: 'pointer' }}
                >
                    Rep. Date
                </div>

                <div
                    title="Report By"
                    onClick={() => sort('reportBy')}
                    className={`text-center col-2 ${sortField === 'reportBy' ? (sortOrder === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                    style={{ cursor: 'pointer' }}
                >
                    Rep. By
                </div>

                <div
                    title="Exposure"
                    onClick={() => sort('exposure')}
                    className={`text-start ps-2 col-5 ${sortField === 'exposure' ? (sortOrder === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                    style={{ cursor: 'pointer' }}
                >
                    Exposure
                </div>

                <div
                    title="Reports/Notes/Files"
                    onClick={() => sort('report')}
                    className={`text-center col-2 ${sortField === 'report' ? (sortOrder === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                    style={{ cursor: 'pointer' }}
                >
                    RNF
                </div>
            </div>

            <div className="scrollable-content">
                {playerData.map((player, index) => (
                    <div
                        className={`nav-top-data ${index === selectedIndex ? 'selected-player' : ''}`}
                        key={index}
                        data-rowindex={index}
                        onClick={(e) => saveYLocation(e, player, index)}
                        style={{ cursor: 'pointer' }}
                    >
                        <div
                            className="col-1 text-center"
                            onClick={() => handleCheckboxChange(player.FBXID)}
                        >
                            <img
                                src={isChecked(player.FBXID) ? checkIcon : checkIconOff}
                                alt="Checkbox Icon"
                                style={{ width: '24px', height: '24px' }}
                            />
                        </div>

                        <div
                            className={`col-5 ps-1 ${player.impnip === 'G' ? 'globalPlayer' : (player.impnip || '').startsWith('V') ? 'brownPlayer' : ''
                                }`}
                            style={{
                                color: player.impnip === 'N' ? 'red' : '',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {player.last?.toUpperCase()}, {player.first?.toUpperCase()} {player.impnip !== '' ? '[' + player.impnip + ']' : ''}
                        </div>

                        <div
                            title={`${player.longSchool || ''} ${player.nickname || ''}`}
                            className="text-center col-2"
                        >
                            {player.school}
                        </div>

                        <div className="text-center col-1">{player.genPos}</div>
                        <div className="text-center col-1">
                            {player.jersey === '0' ? '' : player.jersey === '-1' ? '0' : player.jersey}
                        </div>
                        <div className="text-center col-1">
                            {player.proJersey === '0' ? '' : player.proJersey === '-1' ? '0' : player.proJersey}
                        </div>

                        <div title={player.heightDate === '0000-00-00' ? '-' : `${player.heightDate} ${player.hwsNote || ''}`} className="text-center col-2">
                            {player.height} {player.heightV}
                        </div>
                        <div title={player.weightDate === '0000-00-00' ? '-' : `${player.weightDate} ${player.hwsNote || ''}`} className="text-center col-2">
                            {player.weight} {player.weightV}
                        </div>
                        <div title={player.speed40FDate === '0000-00-00' ? '-' : `${player.speed40FDate} ${player.hwsNote || ''}`} className="text-center col-2">
                            {player.speed40Fastest} {player.speed40FVerified}
                        </div>

                        <div
                            className={`text-center col-2 ${player.cflTeam && player.cflTeam !== 'null' ? 'cfl' : ''}`}
                            title={
                                player.cflTeam && player.cflTeam !== 'null'
                                    ? `${FindCFLTeam('cfl', player.cflTeam) || 'Unknown Team'} - ${player.cflPos || ''} - ${FindStatus('cfl', player.cflStatus) || ''} - ${player.cflDate || ''}`.trim()
                                    : ''
                            }
                        >
                            {player.cflTeam && player.cflTeam !== 'null' ? player.cflTeam : ''} {player.cflStatus}
                        </div>

                        <div className={`text-center col-2 ${player.cflDate && player.cflDate !== '-' ? 'cfl' : ''}`}>
                            {player.cflDate}
                        </div>
                        <div className={`text-center col-1 ${player.optionYr && player.optionYr !== 'null' ? 'cfl' : ''}`}>
                            {player.optionYr}
                        </div>
                        <div className={`text-center col-1 ${player.cflDraftYear && player.cflDraftYear !== 'null' ? 'cfl' : ''}`}>
                            {player.cflDraftYear}
                        </div>

                        <div
                            className={`text-center col-2 ${player.nflTeam && player.nflTeam !== 'null' ? 'nfl' : ''}`}
                            title={
                                player.nflTeam && player.nflTeam !== 'null'
                                    ? `${FindCFLTeam('nfl', player.nflTeam) || 'Unknown Team'} - ${player.nflPos || ''} - ${FindStatus('nfl', player.nflStatus) || ''} - ${player.nflDate || ''}`.trim()
                                    : ''
                            }
                        >
                            {player.nflTeam && player.nflTeam !== 'null' ? player.nflTeam : ''} {player.nflStatus}
                        </div>

                        <div className={`text-center col-2 ${player.nflDate && player.nflDate !== '-' ? 'nfl' : ''}`}>
                            {player.nflDate}
                        </div>
                        <div className={`text-center col-1 ${player.nflDraftYear && player.nflDraftYear !== 'null' ? 'nfl' : ''}`}>
                            {player.nflDraftYear}
                        </div>

                        <div
                            className={`text-center col-3 ${player.ufl24Team && player.ufl24Team !== 'null' ? 'ufl24' : ''}`}
                            title={
                                player.ufl24Team && player.ufl24Team !== 'null'
                                    ? `${FindCFLTeam('ufl', player.ufl24Team) || 'Unknown Team'} - ${player.ufl24Pos || ''} - ${FindStatus('ufl', player.ufl24Status) || ''} - ${player.ufl24Date || ''}`.trim()
                                    : ''
                            }
                        >
                            {player.ufl24Team && player.ufl24Team !== 'null' ? player.ufl24Team : ''} {player.ufl24Status}
                        </div>

                        <div
                            className={`text-center col-2 ${player.theEvent && player.theEvent !== 'null' ? 'event' : ''}`}
                            title={
                                player.theEvent && player.theEvent !== 'null'
                                    ? `${FindCFLTeam('event', player.theEvent) || 'Unknown Team'} - ${player.eventPos || ''} - ${player.theEventDate || ''} - ${player.eventSide || ''}`.trim()
                                    : ''
                            }
                        >
                            {player.theEvent !== 'null' ? player.theEvent : ''}
                        </div>

                        <div className="text-center col-1">{player.calculated}</div>
                        <div className="text-center col-1">{player.finalOut}</div>
                        <div className="text-center col-3">{player.reportDate ? player.reportDate : '-'}</div>
                        <div className="text-center col-2">{player.reportBy ? player.reportBy : '-'}</div>

                        <div
                            className="text-start ps-1 col-5"
                            title={player.exposure ? player.exposure : 'No Exposure'}
                        >
                            {player.exposure ? player.exposure : 'No Exposure'}
                        </div>

                        <div className="text-center col-2">{player.report} {player.note} {player.pdf}</div>
                    </div>
                ))}
            </div>

            {rapidListID && activePlayer && (
                <AddToBox
                    player={activePlayer}
                    style={{
                        position: 'absolute',
                        top: boxPosition.top,
                        left: boxPosition.left,
                        width: '350px',
                        zIndex: 1000,
                        height: '400px',
                    }}
                    onClose={closeAddToBox}
                />
            )}
        </div>
    );
};

export default ScoutOneLiner;
